import React, { useEffect, useRef, useState } from 'react';
import { HeroSection } from '../components/HeroSection';
import { HeroSectionB } from '../components/HeroSectionB';
import { HeroSectionKit } from '../components/HeroSectionKit';
import { HeroSectionCards } from '../components/HeroSectionCards';
import { HeroSectionStory } from '../components/HeroSectionStory';
import { IncludesCarousel } from '../components/IncludesCarousel';
import { SignalSection } from '../components/SignalSection';
import { SymptomsSection } from '../components/SymptomsSection';
import { SignalBridge } from '../components/SignalBridge';
import { OfferSection } from '../components/OfferSection';
import { GiftDay1Section } from '../components/GiftDay1Section';
type DeferredProps = {
  load: () => Promise<any>;
  minHeight?: number;
  id?: string;
};
function DeferredSection({
  load,
  minHeight = 420,
  id
}: DeferredProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    const loadNow = () => {
      load().then(module => {
        if (!cancelled) {
          const exported = module.default ?? Object.values(module).find(value => typeof value === 'function');
          if (exported) setComponent(() => exported as React.ComponentType);
        }
      }).catch(() => {});
    };
    if (!('IntersectionObserver' in window)) {
      loadNow();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        observer.disconnect();
        loadNow();
      }
    }, {
      rootMargin: '1000px 0px'
    });
    observer.observe(host);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [load]);
  return <div id={id} ref={hostRef} style={{
    minHeight: Component ? undefined : minHeight
  }} aria-busy={!Component} data-id="element-812" data-mp="21w7x8ga0pr0z">
      {Component ? <Component data-id="element-813" data-mp="356cco9ieuntd" /> : null}
    </div>;
}
const loadPain = () => import('../components/PainSection').then(m => ({
  default: m.PainSection
}));
const loadBenefits = () => import('../components/BenefitsSection').then(m => ({
  default: m.BenefitsSection
}));
const loadProtocol = () => import('../components/ProtocolSection').then(m => ({
  default: m.ProtocolSection
}));
const loadQuiz = () => import('../components/QuizSection').then(m => ({
  default: m.QuizSection
}));
const loadDifference = () => import('../components/DifferenceSection').then(m => ({
  default: m.DifferenceSection
}));
const loadTestimonials = () => import('../components/TestimonialsSection').then(m => ({
  default: m.TestimonialsSection
}));
const loadHowItWorks = () => import('../components/HowItWorksSection').then(m => ({
  default: m.HowItWorksSection
}));
const loadForWho = () => import('../components/ForWhoSection').then(m => ({
  default: m.ForWhoSection
}));
const loadIdentity = () => import('../components/IdentityTransitionSection').then(m => ({
  default: m.IdentityTransitionSection
}));
const loadOffer = () => import('../components/OfferSection').then(m => ({
  default: m.OfferSection
}));
const loadFAQ = () => import('../components/FAQSection').then(m => ({
  default: m.FAQSection
}));
const loadFinalCTA = () => import('../components/FinalCTASection').then(m => ({
  default: m.FinalCTASection
}));
export function LandingPage() {
  const params = typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search);
  // Hero por defecto: cards (Protocolo H.E.R. protagonista + carrusel "Todo lo que incluye H.E.R." que se mueve
  // solo), y "Tu cuerpo no falló" como sección quieta justo debajo.
  // Variantes: ?v=story (relato animado de ~6 s antes de H.E.R.), ?v=kit (composición estática),
  // ?v=a (hero original) y ?v=b (variante B).
  // Sección "Probá tu Día 1 gratis" entre FAQ y el cierre: visible por defecto; ?regalo=0 la oculta.
  const variant = params.get('v') ?? 'cards';
  const showGift = params.get('regalo') !== '0';
  const previewPostPurchase = params.get('preview') === 'postpurchase';
  if (previewPostPurchase) {
    return <main className="min-h-screen bg-background text-foreground font-body" data-id="element-814" data-mp="37024esepbl4r"><OfferSection data-id="element-815" data-mp="322tj4iy9b0za" /></main>;
  }
  const isStory = variant === 'story';
  const isTopHero = variant === 'cards' || variant === 'kit' || isStory;
  // Orden de las secciones (después del hero): Pain → Benefits → Método → Quiz → ¿En qué momento estás? (+ video)
  // → De la improvisación a una estrategia → Para quién → Identidad → Oferta → FAQ → Regalo Día 1 → Cierre.
  return <main className="overflow-hidden bg-background text-foreground min-h-screen font-body" data-id="element-816" data-mp="2j3fodyspdm7q">
      {isStory && <HeroSectionStory data-id="element-817" data-mp="mb5tz9phicr1" />}
      {isStory && <div className="bg-gradient-hero pt-8" data-id="element-818" data-mp="ma2y0ndb1k49"><IncludesCarousel data-id="element-819" data-mp="m91mkhsdmvm0" /></div>}
      {variant === 'cards' && <HeroSectionCards data-id="element-820" data-mp="hd4aino5cbf1" />}
      {variant === 'kit' && <HeroSectionKit data-id="element-821" data-mp="ha8bew0kbgp0" />}
      {!isStory && <SignalSection data-id="element-822" data-mp="ptv2ppe7kpp7" />}
      {!isStory && <SymptomsSection data-id="element-823" data-mp="pstgqpvnmisp" />}
      {!isStory && <SignalBridge data-id="element-824" data-mp="tuezusys7tbc" />}
      {isTopHero ? null : variant === 'b' ? <HeroSectionB data-id="element-825" data-mp="tsu798w12kg4" /> : <HeroSection data-id="element-826" data-mp="tsam7jb2zfxv" />}
      <DeferredSection load={loadPain} minHeight={420} data-id="element-827" data-mp="ylncryzblwt9" />
      <DeferredSection load={loadBenefits} minHeight={360} data-id="element-828" data-mp="ym5e3a0ub181" />
      <DeferredSection load={loadProtocol} minHeight={620} data-id="element-829" data-mp="13kq5cjy46akc" />
      <DeferredSection load={loadQuiz} minHeight={620} data-id="element-830" data-mp="13mb2mhbtc7lw" />
      <DeferredSection load={loadHowItWorks} minHeight={480} data-id="element-831" data-mp="6virly3ql5qz" />
      <DeferredSection load={loadTestimonials} minHeight={480} data-id="element-832" data-mp="av9fnu1vp7rt" />
      <DeferredSection load={loadDifference} minHeight={620} data-id="element-833" data-mp="axvory4f3vc5" />
      <DeferredSection load={loadForWho} minHeight={520} data-id="element-834" data-mp="fu48lotk8cpn" />
      <DeferredSection load={loadIdentity} minHeight={480} data-id="element-835" data-mp="ktp7y0cn6n2w" />
      <DeferredSection load={loadOffer} minHeight={720} id="offer" data-id="element-836" data-mp="kva23ov1o408" />
      <DeferredSection load={loadFAQ} minHeight={560} data-id="element-837" data-mp="1r8zm37gh084m" />
      {showGift && <GiftDay1Section data-id="element-838" data-mp="1w8vpb4hcfvj4" />}
      <DeferredSection load={loadFinalCTA} minHeight={560} data-id="element-839" data-mp="1w8cgqedl2jhr" />
    </main>;
}